import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, Box, Grid } from "@mui/material";
import { AgeDistributionData } from "../../types";
import { ageDistributionData } from "../../data/impactData";
import CircularProgress from "./CircularProgress";
interface AgeDistributionProps {
  data: AgeDistributionData;
  useVerbiage: "state" | "organization" | "nationwide";
  stateOrOrganization: string;
  isExport?: boolean;
}

const AgeDistribution: React.FC<AgeDistributionProps> = ({
  data,
  useVerbiage,
  stateOrOrganization,
  isExport = false,
}) => {
  const [ageOrder, setAgeOrder] =
    useState<typeof ageDistributionData>(ageDistributionData);

  const getVerbiage = () => {
    let verbiage =
      useVerbiage === "state"
        ? `in ${stateOrOrganization}`
        : useVerbiage === "organization"
        ? `by ${stateOrOrganization}`
        : "nationwide";

    let subtitle =
      useVerbiage === "state"
        ? `Age distribution data from forensic interviews conducted ${verbiage}, supporting tailored services and interview practices.`
        : useVerbiage === "organization"
        ? "Understanding age patterns helps tailor appropriate support services and interview approaches."
        : "Age distribution data from forensic interviews conducted nationwide, supporting tailored services and interview practices.";

    return [
      `Age distribution data from interviews conducted ${verbiage}`,
      subtitle,
    ];
  };

  useEffect(() => {
    ageDistributionData.map((a) => {
      const found = data.categories.find(
        (c) => c.name === a.name.split(" ")[0]
      );
      if (found) {
        a.value = Math.floor(found.value) + "%";
      }
    });
    const sortedAgeData = [...ageDistributionData].sort((a, b) => {
      const aValue = parseInt(a.value.replace("%", ""));
      const bValue = parseInt(b.value.replace("%", ""));
      return bValue - aValue;
    });

    // Sort the last 3 items in the array by age from lowest to highest
    const lastThree = sortedAgeData.slice(-3).sort((a, b) => {
      const aAge = parseInt(a.name.split("-")[0]);
      const bAge = parseInt(b.name.split("-")[0]);
      return aAge - bAge;
    });

    const finalSortedData = [...sortedAgeData.slice(0, -3), ...lastThree];
    setAgeOrder(finalSortedData);
  }, [ageDistributionData]);

  return (
    <Card>
      <CardContent
        className={isExport ? "export-card-content" : ""}
        sx={{ p: isExport ? 0 : 3 }}
      >
        <Typography
          variant={isExport ? "body1" : "h5"}
          component="h2"
          sx={{
            mb: isExport ? 0 : 1,
            fontWeight: 600,
            textAlign: isExport ? "center" : "left",
          }}
        >
          Age Distribution of People Interviewed
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, display: isExport ? "none" : "block" }}
        >
          {getVerbiage()[1]}
        </Typography>

        <Grid container>
          <Box
            sx={{
              width: "100%",
              height: isExport ? 130 : "auto",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                textAlign: "center",
                justifyContent: "space-around",
                width: "100%",
                height: "auto",
                mt: isExport ? 2 : 0,
                px: isExport ? 2 : 0,
              }}
            >
              <Box>
                <Typography
                  variant={isExport ? "h4" : "h2"}
                  component="div"
                  sx={{
                    fontWeight: 900,
                    color: "#582a77",
                    fontSize: isExport ? "2rem" : "4rem",
                  }}
                >
                  {ageOrder[0]?.value}
                </Typography>
                <Typography
                  variant={isExport ? "body2" : "body1"}
                  color="text.primary"
                  sx={{
                    fontWeight: 500,
                    fontSize: isExport ? "0.5rem" : "0.875rem",
                  }}
                >
                  of forensic interviews involved
                  <br />
                  individuals between the
                  <br />
                  <b>{ageOrder[0]?.desc}</b>
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant={isExport ? "h4" : "h2"}
                  component="div"
                  sx={{
                    fontWeight: 900,
                    color: "#8a4b9d",
                    fontSize: isExport ? "2rem" : "4rem",
                  }}
                >
                  {ageOrder[1]?.value}
                </Typography>
                <Typography
                  variant={isExport ? "body2" : "body1"}
                  color="text.primary"
                  sx={{
                    fontWeight: 500,
                    fontSize: isExport ? "0.5rem" : "0.875rem",
                  }}
                >
                  of forensic interviews involved
                  <br />
                  individuals between the
                  <br />
                  <b>{ageOrder[1]?.desc}</b>
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: isExport ? 120 : "auto",
              mt: isExport ? -2 : 4,
              display: "flex",
              flexDirection: "row",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: "33%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: isExport ? 0 : 1,
              }}
            >
              <CircularProgress
                percentage={+ageOrder[2]?.value.slice(0, -1)}
                color={"#582a77"}
                size={isExport ? 100 : 140}
              />
              <Typography
                variant={isExport ? "body2" : "body1"}
                color="#4D607F"
                sx={{
                  fontWeight: 700,
                  fontSize: isExport ? "0.75rem" : 16,
                }}
              >
                {ageOrder[2]?.name}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "33%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: isExport ? 0 : 1,
              }}
            >
              <CircularProgress
                percentage={+ageOrder[3]?.value.slice(0, -1)}
                color={"#8A4B9D"}
                size={isExport ? 100 : 140}
              />
              <Typography
                variant={isExport ? "body2" : "body1"}
                color="#4D607F"
                sx={{
                  fontWeight: 700,
                  fontSize: isExport ? "0.75rem" : 16,
                }}
              >
                {ageOrder[3]?.name}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "33%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: isExport ? 0 : 1,
              }}
            >
              <CircularProgress
                percentage={+ageOrder[4]?.value.slice(0, -1)}
                color={"#CCAFE7"}
                size={isExport ? 100 : 140}
              />
              <Typography
                variant={isExport ? "body2" : "body1"}
                color="#4D607F"
                sx={{
                  fontWeight: 700,
                  fontSize: isExport ? "0.75rem" : 16,
                }}
              >
                {ageOrder[4]?.name}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: isExport ? 0 : 2, textAlign: "center" }}
        >
          {getVerbiage()[0]}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AgeDistribution;
